import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetStudentGyms, useGetStudentMasterCalendar, type MasterCalendarResponseTrainersItem, type MasterCalendarResponseTrainersItemSlotsItem } from "@workspace/api-client-react";
import { useState, useMemo } from "react";
import { Calendar, Clock, MapPin, User, Building2, Filter } from "lucide-react";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime(t: string) {
  const [hStr, mStr] = t.split(":");
  const h = parseInt(hStr);
  const m = mStr || "00";
  if (h === 0) return `12:${m} AM`;
  if (h === 12) return `12:${m} PM`;
  if (h > 12) return `${h - 12}:${m} PM`;
  return `${h}:${m} AM`;
}

export default function MasterCalendar() {
  const [selectedGymId, setSelectedGymId] = useState<string | undefined>(undefined);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);

  const { data: studentGyms, isLoading: gymsLoading } = useGetStudentGyms();
  const { data: calendar, isLoading: calendarLoading } = useGetStudentMasterCalendar(
    { gymId: selectedGymId, locationId: selectedLocationId },
    { query: { enabled: true } }
  );

  const sortedTrainers = useMemo(() => {
    if (!calendar?.trainers) return [];
    return [...calendar.trainers].sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));
  }, [calendar?.trainers]);

  const slotsByDay = useMemo(() => {
    const days: Record<number, { trainer: MasterCalendarResponseTrainersItem; slot: MasterCalendarResponseTrainersItemSlotsItem }[]> = {};
    for (let d = 0; d < 7; d++) days[d] = [];
    for (const trainer of sortedTrainers) {
      for (const slot of trainer.slots || []) {
        if (slot.dayOfWeek !== undefined && slot.dayOfWeek !== null) {
          days[slot.dayOfWeek]?.push({ trainer, slot });
        }
      }
    }
    for (const d of Object.keys(days)) {
      days[Number(d)].sort((a, b) => (a.slot.startTime || "").localeCompare(b.slot.startTime || ""));
    }
    return days;
  }, [sortedTrainers]);

  const today = new Date().getDay();

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Gym Calendar</h1>
            <p className="text-muted-foreground text-lg">Trainer availability at your home gym(s)</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {!gymsLoading && studentGyms && studentGyms.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={!selectedGymId ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setSelectedGymId(undefined); setSelectedLocationId(undefined); }}
                  className={`rounded-lg text-xs h-8 ${!selectedGymId ? "bg-primary text-black" : "border-white/10 hover:bg-white/5"}`}
                >
                  All Gyms
                </Button>
                {studentGyms.map((sg) => (
                  <Button
                    key={sg.gymId}
                    variant={selectedGymId === sg.gymId ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSelectedGymId(sg.gymId); setSelectedLocationId(undefined); }}
                    className={`rounded-lg text-xs h-8 ${selectedGymId === sg.gymId ? "bg-primary text-black" : "border-white/10 hover:bg-white/5"}`}
                  >
                    {sg.gym?.name || "Gym"} {sg.isPrimary && "★"}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {calendar?.locations && calendar.locations.length > 0 && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={!selectedLocationId ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLocationId(undefined)}
                  className={`rounded-lg text-xs h-8 ${!selectedLocationId ? "bg-primary text-black" : "border-white/10 hover:bg-white/5"}`}
                >
                  All Locations
                </Button>
                {calendar.locations.map((loc) => (
                  <Button
                    key={loc.id}
                    variant={selectedLocationId === loc.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedLocationId(loc.id)}
                    className={`rounded-lg text-xs h-8 ${selectedLocationId === loc.id ? "bg-primary text-black" : "border-white/10 hover:bg-white/5"}`}
                  >
                    {loc.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {gymsLoading || calendarLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white/5 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : !studentGyms || studentGyms.length === 0 ? (
          <Card className="glass-panel border-white/5">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-white font-medium mb-2">No home gym selected</p>
              <p className="text-sm text-muted-foreground mb-4">Add a home gym from your profile to see trainer availability.</p>
              <a href="/profile">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-black">
                  Go to Profile
                </Button>
              </a>
            </CardContent>
          </Card>
        ) : sortedTrainers.length === 0 ? (
          <Card className="glass-panel border-white/5">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-white font-medium mb-2">No availability found</p>
              <p className="text-sm text-muted-foreground">No trainers have posted availability at your home gym(s) yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {DAY_NAMES.map((dayName, dayIndex) => {
              const daySlots = slotsByDay[dayIndex] || [];
              if (daySlots.length === 0) return null;
              const isToday = dayIndex === today;
              return (
                <Card key={dayIndex} className={`glass-panel border-white/5 ${isToday ? "ring-1 ring-primary/30" : ""}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-display text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      {dayName}
                      {isToday && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-normal">Today</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {daySlots.map(({ trainer, slot }, idx) => (
                      <div
                        key={`${trainer.id}-${slot.id}-${idx}`}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-bold text-white shrink-0 overflow-hidden">
                          {trainer.avatarUrl ? (
                            <img src={trainer.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">
                            {trainer.firstName} {trainer.lastName}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                            </span>
                            {slot.locationName && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {slot.locationName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
